import React, { useState, useEffect, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import "./LatestPosts.css";
import { FaList, FaTh, FaEdit, FaTrash } from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";
import { LanguageContext } from "../context/LanguageContext"; // Import LanguageContext

// --- Translatable Content Definitions ---
const enContent = {
  latestPostsTitle: "Latest Posts",
  categories: {
    Announcements: "Announcements",
    NewsEvents: "News/Events",
    Admissions: "Admissions",
    Other: "Other",
    MyPosts: "My Posts",
  },
  readOnline: "Read Online",
  download: "Download",
  noPostsFound: (category) => `No posts found for "${category}".`,
  deleteDialogTitle: "Delete this post?",
  deleteDialogDescription:
    "Are you sure you want to delete this post? This action cannot be undone.",
  cancelButton: "Cancel",
  deleteButton: "Delete",
  postDeletedSuccess: "Post deleted successfully!",
  postDeleteFailed: (error) => `Failed to delete post: ${error}`,
  postDeleteError: "An error occurred while deleting the post.",
  listView: "List View", // for aria-label
  cardView: "Card View", // for aria-label
};

const urContent = {
  latestPostsTitle: "تازہ ترین پوسٹس", // Latest Posts
  categories: {
    Announcements: "اعلانات", // Announcements
    NewsEvents: "خبریں/واقعات", // News/Events
    Admissions: "داخلے", // Admissions
    Other: "دیگر", // Other
    MyPosts: "میری پوسٹس", // My Posts
  },
  readOnline: "آن لائن پڑھیں", // Read Online
  download: "ڈاؤن لوڈ کریں", // Download
  noPostsFound: (category) => `"${category}" کے لیے کوئی پوسٹ نہیں ملی۔`, // No posts found for "Category".
  deleteDialogTitle: "کیا اس پوسٹ کو حذف کریں؟", // Delete this post?
  deleteDialogDescription:
    "کیا آپ واقعی اس پوسٹ کو حذف کرنا چاہتے ہیں؟ یہ عمل واپس نہیں کیا جا سکتا۔", // Are you sure you want to delete this post? This action cannot be undone.
  cancelButton: "منسوخ کریں", // Cancel
  deleteButton: "حذف کریں", // Delete
  postDeletedSuccess: "پوسٹ کامیابی سے حذف ہو گئی!", // Post deleted successfully!
  postDeleteFailed: (error) => `پوسٹ کو حذف کرنے میں ناکامی: ${error}`, // Failed to delete post: {error}
  postDeleteError: "پوسٹ حذف کرتے وقت ایک خرابی پیش آئی۔", // An error occurred while deleting the post.
  listView: "فہرست کا منظر", // List View
  cardView: "کارڈ کا منظر", // Card View
};
// --- End Translatable Content Definitions ---

const LatestPosts = () => {
  const [viewMode, setViewMode] = useState("list");
  const [selectedCategory, setSelectedCategory] = useState("Announcements");
  const [posts, setPosts] = useState([]);
  const { isLoggedIn } = useContext(AuthContext);
  const navigate = useNavigate();
  const [alert, setAlert] = useState({ message: "", severity: "" });

  const [openDialog, setOpenDialog] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);

  const currentUser = JSON.parse(localStorage.getItem("user"));

  const { language } = useContext(LanguageContext); // Consume LanguageContext
  const content = language === "en" ? enContent : urContent; // Select content based on language

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/posts`);
        const data = await response.json();
        setPosts(data);
      } catch (error) {
        console.error("Error fetching posts:", error);
      }
    };

    fetchPosts();
  }, []);

  // Map original categories to their translated versions for rendering
  const categories = [
    "Announcements",
    "News/Events",
    "Admissions",
    "Other",
    "My Posts",
  ];

  const translatedCategories = categories.map((cat) => {
    switch (cat) {
      case "Announcements":
        return content.categories.Announcements;
      case "News/Events":
        return content.categories.NewsEvents;
      case "Admissions":
        return content.categories.Admissions;
      case "Other":
        return content.categories.Other;
      case "My Posts":
        return content.categories.MyPosts;
      default:
        return cat;
    }
  });

  const toggleViewMode = (mode) => {
    setViewMode(mode);
  };

  const handleCategoryClick = (categoryKey) => {
    // When clicking, we need to find the original English key
    // to match against the 'posts' data which is likely in English.
    const originalCategory = Object.keys(enContent.categories).find(
      (key) =>
        enContent.categories[key] === categoryKey ||
        urContent.categories[key] === categoryKey
    );

    if (originalCategory === "MyPosts") {
      setSelectedCategory("My Posts");
    } else if (originalCategory === "NewsEvents") {
      setSelectedCategory("News/Events");
    } else {
      setSelectedCategory(originalCategory || categoryKey); // Fallback to key if not found
    }
  };

  const handleClickOpenDialog = (postId) => {
    setPostToDelete(postId);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setPostToDelete(null);
  };

  const confirmDelete = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/posts/${postToDelete}`,
        { method: "DELETE" }
      );

      if (response.ok) {
        setPosts(posts.filter((post) => post._id !== postToDelete));
        setAlert({
          message: content.postDeletedSuccess, // Translated message
          severity: "success",
        });
      } else {
        const errorData = await response.json();
        setAlert({
          message: content.postDeleteFailed(
            errorData.message || response.statusText
          ), // Translated message
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error deleting post:", error);
      setAlert({
        message: content.postDeleteError, // Translated message
        severity: "error",
      });
    } finally {
      setTimeout(() => setAlert({ message: "", severity: "" }), 4000);
      setOpenDialog(false);
      setPostToDelete(null);
    }
  };

  // Filtering logic remains based on the *original* English category to match API data
  const filteredPosts =
    selectedCategory === "My Posts" && currentUser
      ? posts.filter((post) => post.userId === currentUser.userId)
      : posts.filter((post) => post.category === selectedCategory);

  return (
    <div className="latest-posts-container">
      {alert.message && (
        <Alert severity={alert.severity} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      <div className="latest-posts-header">
        <h1 data-tts="true">{content.latestPostsTitle}</h1>{" "}
        {/* Translated title */}
      </div>

      <div className="categories-nav">
        {categories.map((categoryKey, index) => {
          // Use categoryKey for original English value
          if (!isLoggedIn && categoryKey === "My Posts") return null;

          const displayCategoryName =
            content.categories[categoryKey.replace(/\s|\//g, "")] ||
            categoryKey; // Get translated name, simplify key to match content object

          return (
            <span
              data-tts="true"
              key={index}
              className={`category-item ${
                selectedCategory === categoryKey ? "active" : "" // Compare with original key
              }`}
              onClick={() => handleCategoryClick(categoryKey)} // Pass original key
            >
              {displayCategoryName} {/* Display translated name */}
            </span>
          );
        })}
      </div>

      <div className="view-mode-toggle">
        <button
          className={`toggle-button ${viewMode === "list" ? "active" : ""}`}
          onClick={() => toggleViewMode("list")}
          aria-label={content.listView} // Translated aria-label
        >
          <FaList size={20} />
        </button>
        <button
          className={`toggle-button ${viewMode === "card" ? "active" : ""}`}
          onClick={() => toggleViewMode("card")}
          aria-label={content.cardView} // Translated aria-label
        >
          <FaTh size={20} />
        </button>
      </div>

      <h2>
        {/* Display selected category, translated if possible.
            Note: selectedCategory will hold the English key, so we translate it for display. */}
        {content.categories[selectedCategory.replace(/\s|\//g, "")] ||
          selectedCategory}
      </h2>

      <div className={`posts-list ${viewMode}-view`}>
        {filteredPosts.length > 0 ? (
          filteredPosts.map((post) => (
            <div key={post._id} className="post-item">
              <div className="post-content">
                <Link to={`/posts/${post._id}`} className="post-title-link">
                  <h3 data-tts="true">{post.title}</h3>
                </Link>
                <p data-tts="true" className="post-subtitle">
                  {post.subtitle}
                </p>

                <div className="post-actions">
                  <Link
                    data-tts="true"
                    to={`/posts/${post._id}`}
                    className="action-button read-online"
                  >
                    {content.readOnline} {/* Translated text */}
                  </Link>
                  <a
                    href={`${process.env.REACT_APP_API_URL}/posts/${post._id}/download`}
                    className="action-button download"
                    data-tts="true"
                  >
                    {content.download} {/* Translated text */}
                  </a>
                </div>

                {post.pdfUrls && post.pdfUrls.length > 0 && (
                  <a
                    href={post.pdfUrls[0]}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download PDF
                  </a>
                )}

                {(currentUser?.role === "admin" ||
                  currentUser?.userId === post.userId) && (
                  <div className="post-actions">
                    <button
                      className="action-icon edit-icon"
                      onClick={() => navigate(`/edit-post/${post._id}`)}
                      aria-label="Edit Post" // Not explicitly translated as it's an icon label
                    >
                      <FaEdit size={18} />
                    </button>
                    <button
                      className="action-icon delete-icon"
                      onClick={() => handleClickOpenDialog(post._id)}
                      aria-label="Delete Post" // Not explicitly translated as it's an icon label
                    >
                      <FaTrash size={18} />
                    </button>
                  </div>
                )}
              </div>

              {post.imageUrls.length > 0 ? (
                <div className="post-image-wrapper">
                  <img
                    data-tts="true"
                    src={post.imageUrls?.[0]}
                    alt={post.title}
                    className="post-image"
                  />
                </div>
              ) : (
                ""
              )}
            </div>
          ))
        ) : (
          <p>
            {content.noPostsFound(
              content.categories[selectedCategory.replace(/\s|\//g, "")] ||
                selectedCategory
            )}
          </p>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {content.deleteDialogTitle}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {content.deleteDialogDescription}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleCloseDialog}>
            {content.cancelButton}
          </Button>
          <Button
            variant="contained"
            onClick={confirmDelete}
            color="error"
            autoFocus
          >
            {content.deleteButton}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LatestPosts;
